import { useEffect, useRef, useState, useMemo } from 'react';
import { loadGoogleMapsScript, getMapsLinkUrl } from '@/lib/googlePlaces';
import { useMapsKey } from '@/lib/MapsKeyContext';
import { Link } from 'react-router-dom';
import { Coffee, MapPin, Star } from 'lucide-react';

// Module-level cache: place_id → { lat, lng }
const locationCache = {};

// Where the map sits before the first marker has been geocoded. Bangkok was
// the launch city, so it is the least surprising opening view — but it is only
// a starting point: the viewport is refit to whatever markers actually resolve,
// so a coffee in Tokyo (or anywhere else) gets framed correctly.
const DEFAULT_CENTER = { lat: 13.746, lng: 100.536 };
const DEFAULT_ZOOM = 13;

// One cafe on its own — or several at the same address — collapses the bounds
// to a single point, and fitBounds would answer that by zooming to building
// level. This is as close as the fit is allowed to get.
const MAX_FIT_ZOOM = 16;
const FIT_PADDING = 48;

export default function MapView({ coffees }) {
  const { status } = useMapsKey();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [selectedCafe, setSelectedCafe] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  // useMemoでcoffeesが変わった時だけ再計算
  const cafeGroups = useMemo(() => {
    const groups = {};
    coffees.forEach((c) => {
      if (!c.place_id) return;
      if (!groups[c.place_id]) {
        groups[c.place_id] = { place_id: c.place_id, name: c.cafe_name, area: c.cafe_area, coffees: [] };
      }
      groups[c.place_id].coffees.push(c);
    });
    return groups;
  }, [coffees]);

  useEffect(() => {
    if (status !== 'ready') return;
    loadGoogleMapsScript().then((google) => {
      if (!mapRef.current) return;
      const map = new google.maps.Map(mapRef.current, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        disableDefaultUI: true,
        zoomControl: true,
        styles: [
          { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        ],
      });
      mapInstanceRef.current = map;
      setMapReady(true);
    });
  }, [status]);

  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return;
    const google = window.google;
    const map = mapInstanceRef.current;

    // Geocoding answers asynchronously, so a later run of this effect has to be
    // able to discard the callbacks the previous one is still waiting on —
    // otherwise a stale result could place a marker or move the viewport after
    // the filter has already changed.
    let cancelled = false;

    // 既存マーカーを非表示（削除せずmap=nullで）
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const cafes = Object.values(cafeGroups);
    const bounds = new google.maps.LatLngBounds();
    let located = 0;
    let pending = cafes.length;

    // Frames every marker. Without this the map stayed on its opening center
    // whatever it was showing, so any cafe outside that one view — a different
    // city, in practice — rendered its marker off-screen and the map looked
    // empty.
    const fitToMarkers = () => {
      // Nothing resolved: an empty result has no sensible frame, so the
      // viewport is left exactly where the user left it rather than snapping
      // back to the default center and losing their place.
      if (cancelled || located === 0) return;
      const northEast = bounds.getNorthEast();
      const southWest = bounds.getSouthWest();
      if (northEast.equals(southWest)) {
        map.setCenter(northEast);
        map.setZoom(MAX_FIT_ZOOM);
        return;
      }
      map.fitBounds(bounds, FIT_PADDING);
      // fitBounds settles the zoom asynchronously, so the cap has to wait for
      // the viewport to come to rest before it can read it back.
      google.maps.event.addListenerOnce(map, 'idle', () => {
        if (cancelled) return;
        if (map.getZoom() > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
      });
    };

    const placeMarker = (cafe, position) => {
      const marker = new google.maps.Marker({
        position,
        map,
        title: cafe.name,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: 'hsl(25, 45%, 28%)',
          fillOpacity: 1,
          strokeColor: 'white',
          strokeWeight: 2,
          scale: 10,
        },
      });
      marker.addListener('click', () => setSelectedCafe(cafe));
      markersRef.current.push(marker);
      bounds.extend(position);
      located += 1;
    };

    // The fit can only run once every cafe has resolved or failed, so each
    // outcome reports back and the last one triggers it.
    const settle = () => {
      pending -= 1;
      if (pending === 0) fitToMarkers();
    };

    cafes.forEach((cafe) => {
      // キャッシュチェック：あればAPIコールなしで即マーカー生成
      if (locationCache[cafe.place_id]) {
        placeMarker(cafe, locationCache[cafe.place_id]);
        settle();
        return;
      }

      // Geocoding API で place_id → 座標を取得
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ placeId: cafe.place_id }, (results, status) => {
        if (cancelled) return;
        if (status === 'OK' && results?.[0]) {
          const location = results[0].geometry.location;
          locationCache[cafe.place_id] = location;
          placeMarker(cafe, location);
        }
        settle();
      });
    });

    return () => { cancelled = true; };
  }, [mapReady, cafeGroups]);

  return (
    <div className="relative">
      <div ref={mapRef} className="w-full h-[calc(100vh-220px)]" />

      {/* Loading / error overlay while the map key is fetched or the map mounts */}
      {status === 'error' ? (
        <div className="absolute inset-0 flex items-center justify-center bg-secondary/30">
          <p className="text-sm text-muted-foreground">Map unavailable</p>
        </div>
      ) : (status !== 'ready' || !mapReady) ? (
        <div className="absolute inset-0 flex items-center justify-center bg-secondary/30">
          <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      ) : null}

      {/* Cafe popup */}
      {selectedCafe && (
        <div className="absolute bottom-4 left-4 right-4 bg-card rounded-2xl border border-border shadow-xl p-4 z-10">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold text-sm">{selectedCafe.name}</h3>
              {selectedCafe.area && (
                <p className="text-xs text-muted-foreground mt-0.5">{selectedCafe.area}</p>
              )}
            </div>
            <button
              onClick={() => setSelectedCafe(null)}
              className="text-muted-foreground hover:text-foreground text-lg leading-none"
            >
              ×
            </button>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {selectedCafe.coffees.map((coffee) => (
              <Link
                key={coffee.id}
                to={`/coffee/${coffee.id}`}
                className="flex items-center justify-between p-2 rounded-xl bg-secondary/40 hover:bg-secondary transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Coffee size={14} className="text-primary" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs font-semibold">{coffee.name}</p>
                    <p className="text-[10px] text-muted-foreground">{coffee.brew_method}</p>
                  </div>
                </div>
                {coffee.average_rating > 0 && (
                  <div className="flex items-center gap-0.5">
                    <Star size={11} className="text-accent fill-current" />
                    <span className="text-[11px] font-semibold">{coffee.average_rating.toFixed(1)}</span>
                  </div>
                )}
              </Link>
            ))}
          </div>
          <a
            href={getMapsLinkUrl(selectedCafe.place_id)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 mt-3 text-xs text-primary font-medium hover:underline"
          >
            <MapPin size={12} />
            Open in Google Maps
          </a>
        </div>
      )}
    </div>
  );
}
